import { useField } from "formik";
import { useCallback, useContext, useEffect, useRef } from "react";

import { Button, FormGroup, MenuItem, AnchorButton } from "@blueprintjs/core";
import { ItemPredicate, ItemRenderer, Select } from "@blueprintjs/select";
import React from "react";
import { useReference, ConfigurationItem } from "hooks/useReference";
import { ProjectVariable, Company, UserInput } from "types";
import { SnContext } from "utils/context";
import { formikCompanyFieldName, getFormikFieldName } from "utils/utils";

type Props = {
    variable: ProjectVariable;
};

export default function ReferenceField({ variable }: Props) {
    const { tableRef, label, required, name } = variable;

    const snContext = useContext(SnContext);
    const snUri = `https://${snContext}.service-now.com`;

    const [company] = useField<Company>(formikCompanyFieldName);
    const companyRef = useRef<Company | undefined>(company.value);

    const [field, , { setValue }] = useField<UserInput>(getFormikFieldName(name));
    const [data, loading, error] = useReference(company.value.id, variable.tableRef!);

    //  clear reference variable on company change
    useEffect(() => {
        if (companyRef.current?.id !== company.value.id) {
            const { type, label } = { ...field.value };
            setValue({ name: "", value: "", type, label });
            companyRef.current = company.value;
        }
    }, [company, field, setValue]);

    const renderReferences: ItemRenderer<ConfigurationItem> = useCallback(
        (reference, { handleClick, modifiers }) => {
            if (!modifiers.matchesPredicate) {
                return null;
            }
            return (
                <MenuItem
                    active={modifiers.active}
                    key={reference.sysId}
                    onClick={handleClick}
                    text={reference.name}
                    //!todo fix <a> descendant
                    labelElement={
                        <AnchorButton
                            intent="primary"
                            minimal
                            icon="link"
                            target="_blank"
                            href={`${snUri}/nav_to.do?uri=${tableRef}.do?sys_id=${reference.sysId}`}
                            // prevent click on parent
                            onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => e.stopPropagation()}
                        />
                    }
                />
            );
        },
        [snUri, tableRef]
    );
    return (
        <>
            <FormGroup
                inline={false}
                label={label}
                labelFor="text-input"
                labelInfo={required ? "*" : undefined}
                disabled={!!error || loading}
            >
                <ReferenceSelect
                    itemPredicate={filterReferences}
                    itemRenderer={renderReferences}
                    items={data ? data : []}
                    onItemSelect={(c) => {
                        const { type, label } = { ...field.value };
                        setValue({ name: c.name, value: c.sysId, type, label });
                    }}
                    noResults={<MenuItem disabled={true} text="No results." />}
                    popoverProps={{ minimal: true, popoverClassName: "custom-select" }}
                    disabled={!!error || loading}
                >
                    <Button
                        loading={loading}
                        text={field.value && field.value.name !== "" ? field.value.name : "..."}
                        rightIcon="caret-down"
                    />
                </ReferenceSelect>
            </FormGroup>
        </>
    );
}

const ReferenceSelect = Select.ofType<ConfigurationItem>();

const filterReferences: ItemPredicate<ConfigurationItem> = (query, reference) => {
    return reference.name.toLowerCase().indexOf(query.toLowerCase()) >= 0;
};
